/*
 * API Lễ tân (/api/v2/reception): phòng, nhân sự, lượt khách.
 * Danh mục giữ trong bộ nhớ, lượt gộp vào store App Tư vấn để các màn sau vẫn đọc cùng một danh sách.
 */
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { getBranches } from '../../api/Branch';
import { BRANCHES } from '../Telesales/dashboard/data';
import { accountIdentity } from '../../utils/accountRole';
import { apiRequest } from '../Telesales/lead/api';
import type { Mang } from '../Telesales/lead/logic';
import { DOCTORS, registerBranchNames, registerStaffNames, type Source, type StaffSt, type Tech, type Visit } from './logic';
import type { BoardRefs } from './reception';
import { mergeServerVisits, namesOfVisit, serverSource, visitFromServer, type ServerQueue, type ServerVisit } from './receptionServer';
import { useTuVan } from './store';

const BASE = '/api/v2/reception';
const POLL_MS = 20_000;
const PER_PAGE = 200;
const MAX_PAGES = 5;

export interface ServerRoom { id: number; branch_id: number; name: string; type: 'consult' | 'procedure'; status: number; current_visit: { id: number; code: string } | null }
export interface ServerStaff { id: number; name: string; reception_role: 'consultant' | 'doctor' | 'technician'; branch_id: number | null; service_areas: Mang[]; duty_status: StaffSt }
export interface TreatmentLine { line_id: number; service_id: number; service_name: string; available: number }
export interface TreatmentDebt { outstanding: number; overdue_amount: number; max_overdue_days: number; has_debt: boolean; blocks_exec: boolean }
export interface TreatmentCard { card_id: number; card_code: string; package_name: string; expires_at?: string | null; remaining_sessions: number; has_debt?: boolean; debt?: TreatmentDebt; lines: TreatmentLine[] }
export interface TreatmentUsage { usage_id: number; card_id: number; card_code: string; line_id: number; service_name: string; line_remaining: number; card_remaining: number; at: string }
export interface VisitTreatment { cards: TreatmentCard[]; need_choice: boolean; no_session: boolean; treatment_mode: 'card' | 'retail' | null; selected_card_id?: number | null; selected_line_id?: number | null; usage?: TreatmentUsage | null }
/** exec-done trả thêm kết quả trừ buổi */
type VisitReply = ServerVisit & { treatment_usage?: TreatmentUsage | null; treatment_need_choice?: boolean };
export interface ActResult { ok: boolean; msg?: string; visit?: Visit; code?: string; raw?: VisitReply }

/** Lỗi nghiệp vụ theo mã trong tài liệu API; lỗi kiểm tra dữ liệu thì dùng nội dung server trả */
const ERR: Record<string, string> = {
  STAFF_NOT_READY: 'Nhân viên đang tạm nghỉ hoặc ngoại tuyến. Chọn người khác.',
  STAFF_INVALID: 'Nhân viên không đúng vai trò hoặc thuộc cơ sở khác.',
  ROOM_BUSY: 'Phòng vừa có khách, đã ngưng dùng hoặc thuộc cơ sở khác. Chọn phòng khác.',
  ROOM_IN_USE: 'Phòng đang có khách, chưa ngưng dùng được.',
  VISIT_STATE_INVALID: 'Lượt vừa được người khác thao tác. Danh sách đã tải lại, vui lòng kiểm tra.',
  VISIT_ACTIVE_EXISTS: 'Khách đang có lượt chưa xong trong ngày.',
  CARD_NO_SESSION: 'Dịch vụ trên thẻ đã hết buổi. Mời khách mua thêm, hoặc chọn thẻ khác, dịch vụ lẻ.',
  CARD_INVALID: 'Thẻ không còn dùng được. Đã tải lại danh sách thẻ, vui lòng chọn lại.',
  USAGE_EXISTS: 'Lượt này đã trừ buổi. Cần admin hoàn buổi trước khi đổi thẻ.',
  USAGE_NOT_FOUND: 'Lượt chưa trừ buổi hoặc đã hoàn rồi. Đã tải lại lượt.',
};

interface ReceptionState {
  branches: { id: number; name: string }[];
  rooms: ServerRoom[];
  staffs: ServerStaff[];
  /** Đã tải xong lần đầu */
  ready: boolean;
  error?: string;
}
export const useReception = create<ReceptionState>(() => ({ branches: [], rooms: [], staffs: [], ready: false }));

const PALETTE = ['#8b5cf6', '#14b8a6', '#f97316', '#22c55e', '#e11d48', '#0284c7', '#a855f7', '#ca8a04'];
const colorOf = (id: number) => PALETTE[id % PALETTE.length];

/* ---------- Danh mục ---------- */

export const curBranch = () => Number(useTuVan.getState().branchId) || 0;

let branchLoad: Promise<void> | undefined;
/** Cơ sở trên server thay cho danh sách một cơ sở trên máy; lần đầu chọn đúng cơ sở cũ theo tên */
const loadBranches = () => {
  branchLoad ??= getBranches().then((res: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    const raw: any[] = Array.isArray(res?.data?.items) ? res.data.items : Array.isArray(res?.data) ? res.data : []; // eslint-disable-line @typescript-eslint/no-explicit-any
    const branches = raw.map((b) => ({ id: Number(b.id), name: String(b.name) }));
    if (!branches.length) throw new Error('empty');
    registerBranchNames(branches.map((b) => [String(b.id), b.name]));
    useReception.setState({ branches });
    const st = useTuVan.getState();
    const oldName = BRANCHES.find((b) => b.id === st.branchId)?.name;
    const pick = branches.find((b) => String(b.id) === st.branchId) ?? branches.find((b) => b.name === oldName) ?? branches[0];
    const id = String(pick.id);
    // Lượt tạo trên máy trước khi nối API đang ghi cơ sở cũ
    const known = (x: string) => branches.some((b) => String(b.id) === x);
    if (id !== st.branchId || st.visits.some((v) => !v.seed && !known(v.branchId))) {
      useTuVan.setState({ branchId: id, visits: st.visits.map((v) => (v.seed || known(v.branchId) ? v : { ...v, branchId: id })) });
    }
  }).catch(() => { branchLoad = undefined; useReception.setState({ error: 'Không tải được danh sách cơ sở' }); });
  return branchLoad;
};

export async function reloadRefs() {
  const b = curBranch();
  if (!b) return;
  const [r, s] = await Promise.all([
    apiRequest<{ items: ServerRoom[] }>(`${BASE}/rooms?branch_id=${b}`),
    apiRequest<{ items: ServerStaff[] }>(`${BASE}/staffs?branch_id=${b}`),
  ]);
  if (b !== curBranch()) return;
  if (!r.ok || !s.ok || !r.data || !s.data) {
    useReception.setState({ error: r.message || s.message || 'Không tải được phòng và nhân sự' });
    return;
  }
  const staffs = s.data.items;
  registerStaffNames(staffs.map((x) => [String(x.id), x.name]));
  useReception.setState({ rooms: r.data.items, staffs, error: undefined });
  const staffSt = Object.fromEntries(staffs.map((x) => [String(x.id), x.duty_status]));
  if (JSON.stringify(staffSt) !== JSON.stringify(useTuVan.getState().staffSt)) useTuVan.setState({ staffSt });
}

/* ---------- Lượt khách ---------- */

/* Mỗi thao tác tăng gen: danh sách đang tải dở (lấy trước thao tác) thì bỏ, không ghi đè kết quả mới */
let gen = 0;

/** Tải lượt trong ngày của cơ sở đang chọn. Tự tính số đếm, bộ lọc, tìm kiếm trên danh sách này để phản hồi ngay */
export async function reloadVisits() {
  const b = curBranch();
  if (!b) return;
  const g = gen;
  const items: ServerVisit[] = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const res = await apiRequest<{ items: ServerVisit[]; pagination: { total: number } }>(`${BASE}/visits?branch_id=${b}&per_page=${PER_PAGE}&page=${page}`);
    if (!res.ok || !res.data) {
      useReception.setState({ error: res.message || 'Không tải được hàng chờ' });
      return;
    }
    items.push(...res.data.items);
    if (items.length >= res.data.pagination.total || !res.data.items.length) break;
  }
  if (g !== gen || b !== curBranch()) return;
  registerStaffNames(items.flatMap(namesOfVisit));
  const st = useTuVan.getState();
  const visits = mergeServerVisits(st.visits, items, Date.now());
  if (visits !== st.visits) useTuVan.setState({ visits });
  useReception.setState({ ready: true });
}

/** Ghi lượt server trả về vào store. localId: lượt trên máy vừa được tạo trên server */
const applyVisit = (sv: ServerVisit, localId?: string): Visit => {
  gen += 1;
  registerStaffNames(namesOfVisit(sv));
  const st = useTuVan.getState();
  if (localId) {
    const cur = st.visits.find((v) => v.id === localId);
    const next = visitFromServer(sv, cur, Date.now());
    useTuVan.setState({ visits: st.visits.map((v) => (v.id === localId ? next : v)) });
    return next;
  }
  useTuVan.setState({ visits: mergeServerVisits(st.visits, [sv], Date.now()) });
  return useTuVan.getState().visits.find((v) => v.serverId === sv.id)!;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const errOf = (r: { code?: string; message: string; body?: any }, fallback: string) => {
  if (r.code === 'CARD_HAS_DEBT') {
    const d = r.body?.data ?? r.body ?? {};
    const money = d.overdue_amount ? ` ${Number(d.overdue_amount).toLocaleString('vi-VN')} đ` : '';
    const days = d.max_overdue_days ? `, quá hạn ${d.max_overdue_days} ngày` : '';
    return `Thẻ ${d.card_code ?? 'đang chọn'} đang nợ${money}${days}. Yêu cầu khách thanh toán, hoặc chọn thẻ khác, dịch vụ lẻ.`;
  }
  return (r.code && ERR[r.code]) || r.message || fallback;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

async function act(id: number, path: string, method: 'POST' | 'PATCH', body: unknown): Promise<ActResult> {
  gen += 1;
  const r = await apiRequest<VisitReply>(`${BASE}/visits/${id}${path}`, { method, body: JSON.stringify(body) });
  if (r.ok && r.data) {
    reloadRefs(); // phòng vừa đổi người ở
    return { ok: true, visit: applyVisit(r.data), raw: r.data };
  }
  reloadVisits();
  reloadRefs();
  return { ok: false, code: r.code, msg: errOf(r, 'Không thực hiện được, vui lòng thử lại.') };
}

/**
 * Lượt chỉ có trên máy (lịch Telesales mẫu, buổi thực hiện, phiếu CSKH, lượt tạo trước khi nối API):
 * tạo trên server khi có thao tác đầu tiên. Khách đã có lượt thì gắn vào lượt đó.
 */
export async function ensureServerVisit(v: Visit): Promise<ActResult> {
  if (v.serverId) return { ok: true, visit: v };
  gen += 1;
  const r = await apiRequest<ServerVisit>(`${BASE}/visits`, {
    method: 'POST',
    body: JSON.stringify({
      branch_id: curBranch(),
      customer_name: v.cust.name,
      ...(v.phoneDigits ? { customer_phone: v.phoneDigits } : {}),
      queue: v.execOwner ? 'exec' : 'consult',
      source: serverSource(v.source),
      mang: v.mang,
      need: v.need,
    }),
  });
  if (r.ok && r.data) return { ok: true, visit: applyVisit(r.data, v.id) };
  if (r.code === 'VISIT_ACTIVE_EXISTS' && v.phoneDigits) {
    const found = await apiRequest<{ items: ServerVisit[] }>(`${BASE}/visits?branch_id=${curBranch()}&q=${v.phoneDigits}`);
    const sv = found.data?.items.find((x) => x.status === 'waiting' || x.status === 'consulting');
    if (sv) return { ok: true, visit: applyVisit(sv, v.id) };
  }
  return { ok: false, code: r.code, msg: errOf(r, 'Không tạo được lượt trên hệ thống.') };
}

const roomIdOf = (name: string | undefined, type: ServerRoom['type']) => (name ? useReception.getState().rooms.find((r) => r.name === name && r.type === type)?.id : undefined);

const onServer = async (v: Visit, then: (id: number) => Promise<ActResult>) => {
  const e = await ensureServerVisit(v);
  return e.ok && e.visit?.serverId ? then(e.visit.serverId) : e;
};

export const assignVisit = (v: Visit, consultant: string, room?: string) => onServer(v, (id) => {
  const roomId = roomIdOf(room, 'consult');
  return act(id, '/assign', 'POST', { consultant_id: Number(consultant), ...(roomId ? { room_id: roomId } : {}) });
});
export const startServerVisit = (v: Visit, consultant: string, room?: string) => onServer(v, (id) => {
  const roomId = roomIdOf(room, 'consult');
  return act(id, '/start', 'POST', { consultant_id: Number(consultant), ...(roomId ? { room_id: roomId } : {}) });
});
export const execVisit = (v: Visit, room: string, tech: string, doctor?: string) => onServer(v, (id) => act(id, '/exec', 'POST', {
  room_id: roomIdOf(room, 'procedure'), tech_id: Number(tech), ...(doctor ? { doctor_id: Number(doctor) } : {}),
}));
export const execDoneVisit = (v: Visit) => onServer(v, (id) => act(id, '/exec-done', 'POST', {}));
export const patchVisit = (v: Visit, body: { owner_tech_id?: number; owner_doctor_id?: number; notified?: boolean; wait?: boolean; queue?: ServerQueue }) =>
  onServer(v, (id) => act(id, '', 'PATCH', body));
export const finishVisit = (v: Visit, status: 'done' | 'cancelled', reason?: string) =>
  onServer(v, (id) => act(id, '/finish', 'POST', { status, ...(reason ? { reason } : {}) }));

/* ---------- Thẻ liệu trình của lượt ---------- */

export const loadTreatment = (serverId: number) => apiRequest<VisitTreatment>(`${BASE}/visits/${serverId}/treatment`);
/** lineId null = dịch vụ lẻ, không trừ thẻ. Lượt đã xong mà chưa trừ thì chọn là trừ luôn */
export const chooseTreatmentLine = async (serverId: number, lineId: number | null): Promise<ActResult> => {
  const r = await apiRequest(`${BASE}/visits/${serverId}/treatment-card`, { method: 'POST', body: JSON.stringify({ line_id: lineId }) });
  return r.ok ? { ok: true } : { ok: false, code: r.code, msg: errOf(r, 'Không chọn được thẻ.') };
};
export const cancelTreatmentUsage = async (serverId: number, reason: string): Promise<ActResult> => {
  const r = await apiRequest(`${BASE}/visits/${serverId}/treatment-usage/cancel`, { method: 'POST', body: JSON.stringify({ reason }) });
  return r.ok ? { ok: true } : { ok: false, code: r.code, msg: errOf(r, 'Không hoàn được buổi.') };
};

export interface WalkInBody { name: string; phone: string; mang: Mang; need: string; source: Source }
export async function createWalkIn(f: WalkInBody): Promise<ActResult> {
  gen += 1;
  const r = await apiRequest<ServerVisit>(`${BASE}/visits`, {
    method: 'POST',
    body: JSON.stringify({ branch_id: curBranch(), customer_name: f.name, customer_phone: f.phone, queue: 'consult', source: serverSource(f.source), mang: f.mang, need: f.need }),
  });
  if (r.ok && r.data) return { ok: true, visit: applyVisit(r.data) };
  return { ok: false, code: r.code, msg: errOf(r, 'Không thêm được khách vào hàng chờ.') };
}

export async function setDuty(staffId: string, status: StaffSt): Promise<ActResult> {
  const r = await apiRequest(`${BASE}/staffs/${staffId}/duty`, { method: 'PATCH', body: JSON.stringify({ status }) });
  if (r.ok) { useTuVan.getState().setStaffSt(staffId, status); return { ok: true }; }
  return { ok: false, msg: errOf(r, 'Không đổi được trạng thái trực.') };
}

/* ---------- Hook ---------- */

/** Tải danh mục và lượt của cơ sở đang chọn, làm mới định kỳ để các máy thấy cùng hàng chờ */
export function useReceptionSync() {
  const branchId = useTuVan((s) => s.branchId);
  useEffect(() => {
    let alive = true;
    const pull = () => loadBranches().then(() => { if (alive) { reloadRefs(); reloadVisits(); } });
    pull();
    const t = setInterval(pull, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [branchId]);
}

/** Phòng và nhân sự của cơ sở đang chọn, theo dạng các bảng Lễ tân đang dùng */
export function useBoardRefs(): BoardRefs {
  const rooms = useReception((s) => s.rooms);
  const staffs = useReception((s) => s.staffs);
  const branchId = useTuVan((s) => s.branchId);
  return useMemo(() => {
    const of = (role: ServerStaff['reception_role']) => staffs.filter((x) => x.reception_role === role);
    return {
      consultRooms: rooms.filter((r) => r.type === 'consult').map((r) => r.name),
      execRooms: rooms.filter((r) => r.type === 'procedure').map((r) => r.name),
      consultants: of('consultant').map((x) => ({ id: String(x.id), name: x.name, branchId, color: colorOf(x.id) })),
      techs: of('technician').map((x): Tech => ({ id: String(x.id), name: x.name, branchId, mangs: x.service_areas ?? [] })),
      doctors: of('doctor').map((x) => ({ id: String(x.id), name: x.name })),
      roomVisits: Object.fromEntries(rooms.map((r) => [r.name, r.current_visit?.id ?? null])),
    };
  }, [rooms, staffs, branchId]);
}

/** Bác sĩ để chọn ở Phiếu tư vấn, Bác sĩ khám: danh sách server, chưa tải được thì danh sách mẫu */
export const useDoctors = () => {
  const { doctors } = useBoardRefs();
  return useMemo(() => {
    const list = doctors.length ? doctors : DOCTORS;
    // Bác sĩ đang mở cơ sở khác: API không trả tên trong danh sách cơ sở đó, vẫn phải chọn được chính mình
    const a = accountIdentity();
    return a.role === 'doctor' && a.id && !list.some((d) => d.id === a.id) ? [...list, { id: a.id, name: a.name || 'Bác sĩ' }] : list;
  }, [doctors]);
};
